import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, ClipboardList, Users, Map, Package,
  FileText, Bot, LogOut, Menu, X, Wrench, Bell, CreditCard, BarChart2,
  Shield, Lock, BookOpen,
} from 'lucide-react';
import { usePermissions } from '../contexts/PermissionContext';
import TrialBanner from './TrialBanner';
import { getBillingStatus } from '../api/client';

// Main nav: permCode = required permission to show the item.
// Falls back to roles[] when permission system is still loading.
const NAV_ITEMS = [
  { path: '/dashboard',    label: 'Dashboard',       icon: LayoutDashboard, permCode: 'dashboard:view',        roles: ['admin', 'manager', 'dispatcher', 'technician'] },
  { path: '/work-orders',  label: 'Work Orders',     icon: ClipboardList,   permCode: 'work_orders:view',      roles: ['admin', 'manager', 'dispatcher', 'technician'] },
  { path: '/technicians',  label: 'Technicians',     icon: Users,           permCode: 'technicians:view',      roles: ['admin', 'manager', 'dispatcher'] },
  { path: '/map',          label: 'GPS Tracking',    icon: Map,             permCode: 'technicians:map:view',  roles: ['admin', 'manager', 'dispatcher'] },
  { path: '/parts',        label: 'Parts Inventory', icon: Package,         permCode: 'parts:view',            roles: ['admin', 'manager'] },
  { path: '/invoices',     label: 'Invoices',        icon: FileText,        permCode: 'invoices:view',         roles: ['admin', 'manager'] },
  { path: '/ai-assistant', label: 'AI Assistant',    icon: Bot,             permCode: 'ai_assistant:view',     roles: ['admin', 'manager', 'technician'] },
  { path: '/billing',      label: 'Billing',         icon: CreditCard,      permCode: 'billing:view',          roles: ['admin'] },
  { path: '/guide',        label: 'User Guide',       icon: BookOpen,        permCode: 'dashboard:view',        roles: ['admin', 'manager', 'dispatcher', 'technician'] },
];

// System-only nav (admin-only, shown at bottom as a separate group)
const SYSTEM_NAV_ITEMS = [
  { path: '/admin/dashboard',  label: 'Admin Stats',     icon: BarChart2, permCode: 'system:admin_stats:view' },
  { path: '/system/roles',     label: 'Role Management', icon: Shield,    permCode: 'system:roles:view' },
  { path: '/system/permissions', label: 'Permissions',   icon: Lock,      permCode: 'system:permissions:view' },
];

export default function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [billing, setBilling] = useState(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const { hasPermission, loading: permLoading, clearPermissions } = usePermissions();

  useEffect(() => {
    getBillingStatus().then(r => setBilling(r.data)).catch(() => {});
  }, [location.pathname]);

  const handleLogout = () => {
    clearPermissions();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  // During permission loading fall back to role-based filtering to avoid flash
  const visibleNav = NAV_ITEMS.filter(item =>
    permLoading
      ? item.roles.includes(user.role)
      : hasPermission(item.permCode)
  );
  const saasAdminEmail = process.env.REACT_APP_SAAS_ADMIN_EMAIL;
  const isSaasAdmin = !saasAdminEmail || user.email === saasAdminEmail;

  const visibleSystemNav = SYSTEM_NAV_ITEMS.filter(item => {
    if (item.path === '/admin/dashboard' && !isSaasAdmin) return false;
    return permLoading ? user.role === 'admin' : hasPermission(item.permCode);
  });

  const SidebarContent = () => (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Logo */}
      <div style={{ padding: '24px 20px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <img src="/logo.png" alt="Service Tech Manager" style={{ width: 44, height: 44, objectFit: 'contain' }} />
          <div>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>Service Tech</div>
            <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11 }}>Manager</div>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: '16px 12px', overflowY: 'auto' }}>
        {visibleNav.map(item => {
          const Icon = item.icon;
          const active = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '10px 12px', borderRadius: 8, marginBottom: 4,
                color: active ? '#fff' : 'rgba(255,255,255,0.7)',
                background: active ? 'rgba(255,255,255,0.15)' : 'transparent',
                textDecoration: 'none', fontSize: 14, fontWeight: active ? 600 : 400,
                transition: 'all 0.15s',
              }}
            >
              <Icon size={18} />
              {item.label}
            </Link>
          );
        })}

        {/* System section — only rendered when user has at least one system perm */}
        {visibleSystemNav.length > 0 && (
          <>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.1em', textTransform: 'uppercase', padding: '16px 12px 6px', marginTop: 4 }}>
              System
            </div>
            {visibleSystemNav.map(item => {
              const Icon = item.icon;
              const active = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 12px', borderRadius: 8, marginBottom: 4,
                    color: active ? '#fff' : 'rgba(255,255,255,0.6)',
                    background: active ? 'rgba(255,255,255,0.12)' : 'transparent',
                    textDecoration: 'none', fontSize: 13, fontWeight: active ? 600 : 400,
                    transition: 'all 0.15s',
                  }}
                >
                  <Icon size={16} />
                  {item.label}
                </Link>
              );
            })}
          </>
        )}
      </nav>

      {/* User */}
      <div style={{ padding: '16px 12px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <div style={{
            width: 36, height: 36, borderRadius: '50%',
            background: '#3B82F6', display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontWeight: 700, fontSize: 14,
          }}>
            {user.full_name?.[0] || 'U'}
          </div>
          <div>
            <div style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>{user.full_name}</div>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, textTransform: 'capitalize' }}>{user.role}</div>
          </div>
        </div>
        <button onClick={handleLogout} style={{
          display: 'flex', alignItems: 'center', gap: 8, width: '100%',
          padding: '8px 12px', border: 'none', borderRadius: 8,
          background: 'rgba(239,68,68,0.15)', color: '#FCA5A5',
          cursor: 'pointer', fontSize: 13,
        }}>
          <LogOut size={15} /> Sign Out
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'clip' }}>
      {/* Desktop Sidebar */}
      <aside style={{
        width: 240, background: '#1E3A8A', flexShrink: 0,
        display: window.innerWidth < 768 ? 'none' : 'flex',
        flexDirection: 'column',
      }}>
        <SidebarContent />
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex' }}>
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)' }} onClick={() => setSidebarOpen(false)} />
          <aside style={{ width: 260, background: '#1E3A8A', position: 'relative', zIndex: 51 }}>
            <button onClick={() => setSidebarOpen(false)} style={{
              position: 'absolute', top: 12, right: 12, background: 'none', border: 'none', color: '#fff', cursor: 'pointer'
            }}>
              <X size={20} />
            </button>
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'clip' }}>
        {/* Topbar */}
        <header style={{
          height: 60, background: '#fff', borderBottom: '1px solid #E5E7EB',
          display: 'flex', alignItems: 'center', padding: '0 20px', gap: 12, flexShrink: 0,
        }}>
          <button onClick={() => setSidebarOpen(true)} style={{
            background: 'none', border: 'none', cursor: 'pointer', display: window.innerWidth >= 768 ? 'none' : 'block',
          }}>
            <Menu size={22} color="#374151" />
          </button>
          <div style={{ flex: 1 }} />
          <button style={{ background: 'none', border: 'none', cursor: 'pointer', position: 'relative' }}>
            <Bell size={20} color="#6B7280" />
          </button>
        </header>

        {/* Trial / status banner */}
        {!bannerDismissed && (
          <TrialBanner billing={billing} onDismiss={() => setBannerDismissed(true)} />
        )}

        {/* Page Content */}
        <main style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
          {children}
        </main>
      </div>
    </div>
  );
}
